import { useCompanyId } from "@context-providers/company-id-provider";
import { TEAM_AVATAR_BACKGROUND } from "@styles/common-colors";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Button, EmailIcon, Skeleton, TeamIcon } from "@flat-ui";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { useTeamUtils } from "@hooks/use-team-utils";
import { Stack } from "@mui-wrapper";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { convertToMemberTypes } from "@store/members/members-slice-utils";
import { fetchingProjectsFlagsSelector, selectedSnapshotMembersSelector } from "@store/projects/projects-selector";
import { inviteMemberToSnapshot } from "@store/projects/projects-slice-thunk";
import { setOne } from "@store/members/members-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { assignableTeamsSelector } from "@store/teams/teams-selector";
import { sortTeams } from "@utils/team-utils";
import { SnapshotEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo } from "react";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";

/** Renders autocomplete to invite members to snapshot */
export function InviteMemberToSnapshot({
  snapshot
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    isTeamName
  } = useTeamUtils();
  const {
    trackEvent
  } = useTrackEvent();
  const companyMembers = useCompanyMembers();
  const teams = useAppSelector(assignableTeamsSelector);
  const {
    respondToUser
  } = useRespondToInvite();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const snapshotMembers = useAppSelector(selectedSnapshotMembersSelector);
  const {
    isAddingSnapshotMember
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const {
    isFetchingCompanyMembers
  } = useAppSelector(fetchingMembersFlagsSelector);
  const snapshotMemberMap = useMemo(() => new Map(snapshotMembers.map(member => [member.identity, member])), [snapshotMembers]);
  const companyMemberIdentities = useMemo(() => new Set(companyMembers.map(companyMember => companyMember.identity)), [companyMembers]);

  /** Reforming company members data for stellar autocomplete component */
  const memberOptions = useMemo(() => sortMembersByNameOrEmail(companyMembers).map(member_0 => {
    const alreadyMemberInSnapshot = snapshotMemberMap.get(member_0.identity);
    const isAlreadyMemberInSnapshot = !!alreadyMemberInSnapshot;
    return {
      title: member_0.name || member_0.email,
      subtitle: FaroCoreApiUtils.Member.isMemberActive(member_0) ? member_0.email : "",
      value: member_0.identity,
      isDisabled: isAlreadyMemberInSnapshot,
      disabledMessage: isAlreadyMemberInSnapshot ? "Already in snapshot" : undefined,
      avatarIcon: FaroCoreApiUtils.Member.isMemberActive(member_0) ? undefined : <EmailIcon />
    };
  }), [companyMembers, snapshotMemberMap]);

  /** Reforming team data for stellar autocomplete component */
  const teamOptions = useMemo(() => sortTeams(teams, "name_ascending").map(team => {
    const alreadyTeamInSnapshot = snapshotMemberMap.get(team.id);
    const isAlreadyTeamInSnapshot = !!alreadyTeamInSnapshot;
    return {
      title: team.name,
      subtitle: `${team.memberCount} members`,
      value: team.id,
      isDisabled: isAlreadyTeamInSnapshot,
      disabledMessage: isAlreadyTeamInSnapshot ? "Already in snapshot" : undefined,
      avatarIcon: <TeamIcon />,
      avatarStyle: {
        bgcolor: TEAM_AVATAR_BACKGROUND
      }
    };
  }), [snapshotMemberMap, teams]);
  const autocompleteOptions = useMemo(() => [...teamOptions, ...memberOptions], [teamOptions, memberOptions]);
  const {
    selectedMembers,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset
  } = useMembersAutocomplete({
    entityName: "snapshot",
    existingMembers: snapshotMembers,
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map(member_1 => member_1.identity)
  });

  /** Disable the `Send Invite` button if no user has been selected and the input is not a valid email */
  const shouldSendInviteDisable = shouldDisableSendInvite(uniqueSelectedValues, isMemberInputValid);

  /** Submit members to be invited */
  async function handleConfirm() {
    trackEvent({
      name: SnapshotEvents.inviteMember,
      props: {
        numberOfMembers: uniqueSelectedValues.length
      }
    });
    const payload = {
      assignments: []
    };
    payload.assignments.push({
      role: "RESOURCE_VIEWER",
      identities: uniqueSelectedValues
    });
    try {
      const response = await dispatch(inviteMemberToSnapshot({
        companyId,
        snapshotId: snapshot.id,
        payload
      })).unwrap();

      // Add new invited member to the company store. response.data.members exist when the response is either success or warning
      if (response.data.members) {
        const newMembersResponse = response.data.members.filter(allMembers => !companyMemberIdentities.has(allMembers.identity));
        newMembersResponse.forEach(newMemberResponse => {
          if (!FaroCoreApiUtils.Member.isTeam(newMemberResponse)) {
            const newMember = convertToMemberTypes(newMemberResponse);

            // The new members should be added to the member store as well.
            dispatch(setOne(newMember));
          }
        });
      }
      respondToUser(response, uniqueSelectedValues.length);
    } catch (error) {
      handleErrorWithToast({
        id: `inviteMemberToSnapshot-${Date.now().toString()}`,
        title: "Cannot invite the member",
        error
      });
    } finally {
      reset();
    }
  }
  if (isFetchingCompanyMembers) {
    return <Skeleton variant="rectangular" height="45px" />;
  }
  return <Stack>
      <MembersAutocomplete isRequired={true} label="Members" options={autocompleteOptions} selectedOptions={selectedMembers} isValid={value => isTeamName(value) || isNewEmailValid(value)} helperText={helperText} handleMemberSelect={handleMemberSelect} onInputChange={onInputChange} />
      {uniqueSelectedValues.length > 0 && <Button variant="ghost" disabled={shouldSendInviteDisable} isLoading={isAddingSnapshotMember} onClick={handleConfirm} sx={{
      mt: SPACE_ELEMENTS_OF_MODAL
    }}>
          Invite
        </Button>}
    </Stack>;
}